import React from 'react';
import { Facebook, Link2, Mail, Clock, Building2, CheckCircle2, Users } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Privacy-safe share buttons — links only ever point to public issue pages.
// Never include names, emails, phones, addresses, or private details.
// ---------------------------------------------------------------------------
export const publicIssueUrl = (trackingNumber) => `${window.location.origin}/issue/${trackingNumber}`;

export const ShareButtons = ({ trackingNumber, title = '', compact = false, className = '' }) => {
  const url = publicIssueUrl(trackingNumber);
  const shareTitle = title ? `${title} | Ward 8 Issue Tracker App (${trackingNumber})` : `Ward 8 Issue Tracker App (${trackingNumber})`;

  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'noopener,noreferrer,width=600,height=500');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Public issue link copied');
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      toast.success('Public issue link copied');
    }
  };

  const shareEmail = () => {
    const subject = encodeURIComponent(shareTitle);
    const body = encodeURIComponent(`I wanted to share this public Ward 8 issue report:\n\n${shareTitle}\n${url}\n\nTracked on the Ward 8 Public Issue Tracker App (PITA). Report it. Map it. Track it. Get answers.`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const size = compact ? 'h-8 px-2 text-[11px]' : 'h-10 px-3 text-xs';
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`} data-testid={`share-buttons-${trackingNumber}`}>
      <Button type="button" variant="outline" onClick={shareFacebook} className={`${size} font-bold border-slate-300 text-[#1a2744] btn-press`} data-testid="share-facebook-btn">
        <Facebook className="h-3.5 w-3.5 mr-1" /> Share
      </Button>
      <Button type="button" variant="outline" onClick={copyLink} className={`${size} font-bold border-slate-300 text-[#1a2744] btn-press`} data-testid="share-copy-btn">
        <Link2 className="h-3.5 w-3.5 mr-1" /> Copy link
      </Button>
      <Button type="button" variant="outline" onClick={shareEmail} className={`${size} font-bold border-slate-300 text-[#1a2744] btn-press`} data-testid="share-email-btn">
        <Mail className="h-3.5 w-3.5 mr-1" /> Email
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Privacy-safe pin/issue badges (days open, City response, resolved, similar)
// ---------------------------------------------------------------------------
export const PinBadges = ({ pin, compact = false }) => (
  <div className={`flex flex-wrap gap-1.5 ${compact ? 'mt-1.5' : 'mt-2'}`} data-testid="pin-badges">
    <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-300 font-semibold text-[11px] gap-1" data-testid="pin-badge-days-open">
      <Clock className="h-3 w-3" /> {pin.days_open} day{pin.days_open === 1 ? '' : 's'} open
    </Badge>
    {pin.is_resolved ? (
      <Badge variant="outline" className="bg-emerald-50 text-emerald-900 border-emerald-300 font-semibold text-[11px] gap-1" data-testid="pin-badge-resolved">
        <CheckCircle2 className="h-3 w-3" /> Resolved
      </Badge>
    ) : pin.response_status === 'awaiting_city_response' ? (
      <Badge variant="outline" className="bg-violet-50 text-violet-900 border-violet-300 font-semibold text-[11px] gap-1" data-testid="pin-badge-awaiting-city">
        <Building2 className="h-3 w-3" /> Awaiting City response
      </Badge>
    ) : pin.response_status === 'city_responded' ? (
      <Badge variant="outline" className="bg-sky-50 text-sky-900 border-sky-300 font-semibold text-[11px] gap-1" data-testid="pin-badge-city-responded">
        <Building2 className="h-3 w-3" /> City responded
      </Badge>
    ) : null}
    {pin.similar_nearby_count > 0 && (
      <Badge variant="outline" className="bg-[#FFF7E6] text-[#7A4E00] border-[#F2D08A] font-semibold text-[11px] gap-1" data-testid="pin-badge-similar-nearby">
        <Users className="h-3 w-3" /> Similar issues nearby: {pin.similar_nearby_count}
      </Badge>
    )}
  </div>
);
